import type { TaskListFilters } from "./api/api";
import { getTaskDayKey } from "./components/taskDateHelpers";
import { routeWeekBounds } from "./state/routePlanner";
import {
  parseDashboardTaskScope,
  parseDateRangeTaskScope,
  type DashboardTaskScope,
  type DateRangeTaskScope,
} from "./taskScopes";
import type { Task } from "./types";

export type TaskRange = {
  scheduledFrom: string;
  scheduledTo: string;
  includeVisitMetrics?: boolean;
};

const taskRangeKey = (range: TaskRange) =>
  `${range.scheduledFrom}..${range.scheduledTo}`;

export const uniqueTaskCacheFilters = (
  filters: Array<TaskListFilters | null | undefined>
): TaskListFilters[] => {
  const byKey = new Map<string, TaskListFilters>();
  filters.filter(Boolean).forEach((filter) => {
    byKey.set(JSON.stringify(filter), filter as TaskListFilters);
  });
  return Array.from(byKey.values());
};

export const taskScheduleRangesForTasks = (
  tasks: Array<Task | null | undefined>
): TaskRange[] => {
  const byKey = new Map<string, TaskRange>();
  tasks.forEach((task) => {
    const dayKey = getTaskDayKey(task?.scheduledFor || task?.createdAt);
    if (!dayKey) return;
    const range = routeWeekBounds(dayKey);
    byKey.set(taskRangeKey(range), range);
  });
  return Array.from(byKey.values());
};

export const loadedDashboardTaskScopes = (
  loadedScopeKeys: string[]
): DashboardTaskScope[] =>
  loadedScopeKeys
    .map(parseDashboardTaskScope)
    .filter((scope): scope is DashboardTaskScope => Boolean(scope));

export const loadedDateRangeTaskScopes = (
  loadedScopeKeys: string[]
): DateRangeTaskScope[] =>
  loadedScopeKeys
    .map(parseDateRangeTaskScope)
    .filter((scope): scope is DateRangeTaskScope => Boolean(scope));

export const taskCacheFiltersForTasks = (
  tasks: Array<Task | null | undefined>,
  loadedScopeKeys: string[] = []
): TaskListFilters[] =>
  uniqueTaskCacheFilters([
    ...taskScheduleRangesForTasks(tasks).map(({ scheduledFrom, scheduledTo }) => ({
      scheduledFrom,
      scheduledTo,
    })),
    ...tasks.flatMap((task) =>
      task
        ? [
            task.machineId ? { machineId: task.machineId } : null,
            task.locationId ? { locationId: task.locationId } : null,
            task.status ? { statuses: [task.status] } : null,
          ]
        : []
    ),
    ...loadedDateRangeTaskScopes(loadedScopeKeys).map(
      ({ scheduledFrom, scheduledTo, includeVisitMetrics }) => ({
        scheduledFrom,
        scheduledTo,
        includeVisitMetrics,
      })
    ),
    ...loadedDashboardTaskScopes(loadedScopeKeys).map(
      ({ scheduledFrom, scheduledTo, overdueTo }) => ({
        scheduledFrom,
        scheduledTo,
        overdueTo,
      })
    ),
  ]);

export const taskCacheFiltersForTaskChanges = (
  previousTasks: Array<Task | null | undefined>,
  nextTasks: Array<Task | null | undefined>,
  loadedScopeKeys: string[] = []
): TaskListFilters[] =>
  taskCacheFiltersForTasks([...previousTasks, ...nextTasks], loadedScopeKeys);

export const taskCacheFiltersForDateRanges = (
  ranges: TaskRange[]
): TaskListFilters[] =>
  uniqueTaskCacheFilters(
    ranges.map(({ scheduledFrom, scheduledTo, includeVisitMetrics }) => ({
      scheduledFrom,
      scheduledTo,
      includeVisitMetrics,
    }))
  );

export const taskCacheFiltersForEntityScopes = ({
  machineIds = [],
  locationIds = [],
  taskIds = [],
}: {
  machineIds?: string[];
  locationIds?: string[];
  taskIds?: string[];
}): TaskListFilters[] =>
  uniqueTaskCacheFilters([
    ...machineIds.map((machineId) => ({ machineId })),
    ...locationIds.map((locationId) => ({ locationId })),
    taskIds.length ? { taskIds } : null,
  ]);
